/**
 * EpisodeProgressDots
 *
 * Displays a horizontal row of dots — one per episode — showing the player's
 * progress through the adventure at a glance.
 *
 * Rules:
 * - Episode 1 (initial authority figure): always shows the authority's avatar
 * - Last episode (resolve / final authority): always shows the authority's avatar
 * - Middle episodes: empty circle until completed, then filled with the
 *   character's avatar (or a solid primary-colour dot if no avatar is available)
 * - The current episode dot is highlighted with a ring
 */

interface EpisodeDot {
  episodeNumber: number;
  /** Avatar URL for this episode's character, or null if not yet available */
  avatarUrl: string | null;
  /** Whether this episode has been completed */
  completed: boolean;
  /** Whether this is the episode the player is currently on */
  isCurrent: boolean;
  /** Whether this is the first episode (authority figure) */
  isFirst: boolean;
  /** Whether this is the last episode (resolve / authority figure) */
  isLast: boolean;
}

interface EpisodeProgressDotsProps {
  /** Total number of episodes in the adventure */
  totalEpisodes: number;
  /** The episode the player is currently on (1-indexed) */
  currentEpisode: number;
  /** Avatar URL for the authority figure (used for episode 1 and last episode) */
  initialCharacterAvatarUrl: string | null;
  /** Completed episodes with their avatar URLs */
  completedEpisodes: Array<{
    episodeNumber: number;
    avatarUrl: string | null;
  }>;
  /** Avatar URL for the current episode's character (from scenarioData) */
  currentEpisodeAvatarUrl?: string | null;
  /**
   * Tailwind ring-offset colour class for the current-episode ring. Defaults to
   * the cream page background; pass a dark value when rendered over a dark
   * (cinematic) surface so the offset gap blends in.
   */
  ringOffsetClassName?: string;
}

export default function EpisodeProgressDots({
  totalEpisodes,
  currentEpisode,
  initialCharacterAvatarUrl,
  completedEpisodes,
  currentEpisodeAvatarUrl,
  ringOffsetClassName = 'ring-offset-[#faf8f5]',
}: EpisodeProgressDotsProps) {
  if (totalEpisodes < 1) return null;

  // Build a lookup map for completed episodes
  const completedMap = new Map<number, string | null>();
  for (const ep of completedEpisodes) {
    completedMap.set(ep.episodeNumber, ep.avatarUrl);
  }

  // Resolve the authority figure's avatar URL with multiple fallbacks:
  // 1. initialCharacterAvatarUrl from the adventure (baseNeutralImageUrl — may not be set)
  // 2. The avatar stored when episode 1 was completed (always available after ep 1 is done)
  // 3. The current episode's avatar (only valid when we're currently on episode 1)
  const authorityAvatarUrl: string | null =
    initialCharacterAvatarUrl
    ?? completedMap.get(1)
    ?? (currentEpisode === 1 ? (currentEpisodeAvatarUrl ?? null) : null);

  // Build the dot data for each episode
  const dots: EpisodeDot[] = Array.from({ length: totalEpisodes }, (_, i) => {
    const episodeNumber = i + 1;
    const isFirst = episodeNumber === 1;
    const isLast = episodeNumber === totalEpisodes;
    const completed = completedMap.has(episodeNumber);
    const isCurrent = episodeNumber === currentEpisode;

    let avatarUrl: string | null = null;

    if (isFirst || isLast) {
      // Authority figure episodes always show the authority avatar
      avatarUrl = authorityAvatarUrl;
    } else if (completed) {
      // Completed middle episodes show the character's avatar
      avatarUrl = completedMap.get(episodeNumber) ?? null;
    } else if (isCurrent && currentEpisodeAvatarUrl) {
      // Current episode shows the current character's avatar
      avatarUrl = currentEpisodeAvatarUrl;
    }

    return {
      episodeNumber,
      avatarUrl,
      completed: completed || isFirst,
      isCurrent,
      isFirst,
      isLast,
    };
  });

  return (
    <div className="flex items-center justify-center gap-0 my-6 px-2" role="list" aria-label="Progresso dos episódios">
      {dots.map((dot, index) => (
        <div key={dot.episodeNumber} className="flex items-center" role="listitem">
          {/* Connector line between dots */}
          {index > 0 && (
            <div
              className={`h-0.5 w-2 sm:w-4 md:w-5 flex-shrink-0 transition-colors duration-300 ${
                dot.completed || dots[index - 1].completed
                  ? 'bg-primary'
                  : 'bg-primary/30'
              }`}
            />
          )}

          {/* The dot itself */}
          <div
            className={`relative flex-shrink-0 rounded-full transition-all duration-300 ${
              dot.isCurrent
                ? `ring-2 ring-primary ring-offset-2 ${ringOffsetClassName}`
                : ''
            } ${
              dot.isFirst || dot.isLast
                ? 'w-8 h-8 sm:w-11 sm:h-11 md:w-14 md:h-14'
                : dot.completed
                  ? 'w-7 h-7 sm:w-10 sm:h-10 md:w-12 md:h-12'
                  : 'w-6 h-6 sm:w-9 sm:h-9 md:w-11 md:h-11'
            }`}
            title={
              dot.isFirst
                ? `Episódio 1 (início)`
                : dot.isLast
                  ? `Episódio ${dot.episodeNumber} (resolução)`
                  : dot.completed
                    ? `Episódio ${dot.episodeNumber} (concluído)`
                    : dot.isCurrent
                      ? `Episódio ${dot.episodeNumber} (actual)`
                      : `Episódio ${dot.episodeNumber}`
            }
          >
            {dot.avatarUrl ? (
              /* Avatar image */
              <img
                src={dot.avatarUrl}
                alt={`Episódio ${dot.episodeNumber}`}
                className={`w-full h-full rounded-full object-cover border-2 ${
                  dot.isFirst || dot.isLast
                    ? 'border-primary'
                    : 'border-primary/60'
                } ${
                  !dot.completed && !dot.isCurrent && !dot.isFirst && !dot.isLast
                    ? 'opacity-40 grayscale'
                    : ''
                }`}
              />
            ) : dot.completed || dot.isFirst || dot.isLast ? (
              /* Filled dot (completed, no avatar) */
              <div
                className={`w-full h-full rounded-full ${
                  dot.isFirst || dot.isLast ? 'bg-primary' : 'bg-primary/70'
                }`}
              />
            ) : (
              /* Empty dot (not yet reached) */
              <div className="w-full h-full rounded-full border-2 border-primary/40 bg-transparent" />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
